/* Built In Imports */
import { forwardRef, useState } from 'react';

/* External Imports */
import {
  Box,
  Button,
  Flex,
  FormControl,
  Grid,
  Link,
  ListItem,
  Radio,
  RadioGroup,
  SimpleGrid,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  UnorderedList,
  useMultiStyleConfig,
  useTab,
} from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import parser from 'html-react-parser';
import { nanoid } from 'nanoid';

/* Internal Imports */
/* Components */
import SingleImage from '@components/Card/SingleImage';
import SectionTitle from '@components/Headings/SectionTitle';
import HtmlBlock from '@components/UI/HtmlBlock';
import SingleLineText from '@components/UI/SingleLineText';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import { refineUrlDomain } from '@components/Utility/utils';
import config from '@config';
import MidContainer from '@Layout/MidContainer';

/* Services */

/**
 *
 * @param {object} section
 * @returns ProgramDetails
 */
export const pageCss = {
  en: {
    tabTitle: {
      fontSize: {
        base: '16px',
        md: '22px',
      },
      color: 'red',
    },
    testimonialHeight: 571,
    cardTitle: {
      lineHeight: {
        base: '1.5rem',
        md: '1.6rem',
      },
    },
  },
  hi: {
    tabTitle: {
      fontSize: {
        base: '16px',
        md: '22px',
      },
      color: 'red',
    },
    testimonialHeight: 571,
    cardTitle: {
      lineHeight: {
        base: '1.5rem',
        md: '1.6rem',
      },
    },
  },
  ta: {
    tabTitle: {
      fontSize: {
        base: '12px',
        md: '22px',
      },
      color: 'red',
    },
    testimonialHeight: 745,
    cardTitle: {
      lineHeight: {
        base: '1.5rem',
        md: '1.6rem',
      },
    },
  },
  te: {
    tabTitle: {
      fontSize: {
        base: '12px',
        md: '18px',
      },
    },
    testimonialHeight: 571,
    cardTitle: {
      lineHeight: {
        base: '1.5rem',
        md: '1.6rem',
      },
    },
  },
  de: {
    tabTitle: {
      fontSize: {
        base: '14px',
        md: '18px',
      },
    },
    testimonialHeight: 571,
    cardTitle: {
      lineHeight: {
        base: '1.5rem',
        md: '1.6rem',
      },
    },
  },
  es: {
    tabTitle: {
      fontSize: {
        base: '14px',
        md: '18px',
      },
    },
    testimonialHeight: 571,
    cardTitle: {
      lineHeight: {
        base: '1.5rem',
        md: '1.6rem',
      },
    },
  },
  fr: {
    tabTitle: {
      fontSize: {
        base: '12px',
        md: '18px',
      },
    },
    testimonialHeight: 571,
    cardTitle: {
      lineHeight: {
        base: '1.5rem',
        md: '1.6rem',
      },
    },
  },
  ru: {
    tabTitle: {
      fontSize: {
        base: '10px',
        md: '18px',
      },
    },
    testimonialHeight: 571,
    cardTitle: {
      lineHeight: {
        base: '1.5rem',
        md: '1.6rem',
      },
    },
  },
  zh: {
    tabTitle: {
      fontSize: { base: '12px', md: '18px' },
    },
    cardTitle: {
      lineHeight: {
        base: '1.5rem',
        md: '1.6rem',
      },
    },
  },
};

const ProgramDetails = ({ section, region, lang, csrData }) => {
  const [prgUrl, setPrgUrl] = useState('');
  const [ctaButtonText, setCtaButtonText] = useState('');
  const [onlinePrgUrl, setOnlinePrgUrl] = useState('');
  const [onlinePrgEventText, setOnlinePrgEventText] = useState('');
  const sectionContent = section?.sectionContent;
  const prgDomain =
    config.ENV_NAME === 'production'
      ? '.sadhguru.org'
      : 'ishaprs.sushumna.isha.in';

  if (csrData && csrData.goy_reg_url === csrData.pbk_reg_url) {
    csrData.pbk_reg_url = csrData.pbk_reg_url + '#!';
  }

  const cardStyle = {
    Open: {
      outline: '1px solid #979797',
      boxShadow: 'none',
      background: '#FFFFFF',
    },
    Selected: {
      outline: '2px solid #CF4520',
      boxShadow:
        '3.06087px 18.7518px 41px rgba(34, 30, 2, 0.08), 1.98389px 12.154px 24.0116px rgba(34, 30, 2, 0.0607407), 1.179px 7.22293px 13.0593px rgba(34, 30, 2, 0.0485926), 0.612173px 3.75037px 6.6625px rgba(34, 30, 2, 0.04), 0.249404px 1.52793px 3.34074px rgba(34, 30, 2, 0.0314074), 0.0566827px 0.347256px 1.61343px rgba(34, 30, 2, 0.0192593)',
      background: '#F5F0E5',
    },
    Full: {
      outline: 'none',
      boxShadow: 'none',
      background: '#F5F0E5',
    },
  };

  const CustomTab = forwardRef((props, ref) => {
    // 1. Reuse the `useTab` hook
    const tabProps = useTab({ ...props, ref });
    const isSelected = !!tabProps['aria-selected'];

    // 2. Hook into the Tabs `size`, `variant`, props
    const styles = useMultiStyleConfig('Tabs', tabProps);
    const Arrow = () => {
      return (
        <Box
          as="span"
          position="absolute"
          width="20px"
          display={isSelected ? 'block' : 'none'}
          height="11px"
          clipPath="polygon(0 0, 50% 100%, 100% 0)"
          left="50%"
          bottom="-10px"
          background="#CF4520"
          // animation={spinAnimation}
          style={{ transition: 'width 1s' }}
          // transform="rotate(45deg)"
          zIndex="0"
          transition="0.4s linear"
        ></Box>
      );
    };

    return (
      <Button
        __css={styles.tab}
        {...tabProps}
        width="50%"
        color={isSelected ? '#FFFFFF !important' : '#979797  !important'}
        bg={isSelected ? '#CF4520' : '#ECECEC'}
        border="0"
        fontFamily="FedraSansStd-book"
        fontStyle="normal"
        fontWeight="600"
        fontSize={pageCss[lang].tabTitle.fontSize}
        // fontSize={{ base: '14px', sm: '22.8514px' }}
        lineHeight="150.3%"
        _active={{ bg: '#CF4520', border: '0' }}
        _focus={{ bg: '#CF4520', border: '0' }}
        pos="relative"
        height={{ base: '45px', sm: '81px' }}
        p={{ base: '0', sm: '1rem' }}
      >
        {/* <Box as="span" mr="2" color={isSelected ? '#FFFFFF' : '#979797'}> */}
        {isSelected ? <Arrow /> : ''}
        {/* </Box> */}
        {tabProps.children}
      </Button>
    );
  });

  let paramLang = config.allLang.indexOf(lang) > -1 ? `/${lang}/` : '';
  let tabDesignHide =
    sectionContent[2]?.titleDecoration === 'tab-hide' ||
    sectionContent[8]?.titleDecoration === 'tab-hide';
  // tabDesignHide = true;

  const onlinecheck = e => {
    if (e?.includes('4-days')) {
      setOnlinePrgEventText('goy_4d_online_register');
    } else {
      setOnlinePrgEventText('goy_2d_online_register');
    }
    setOnlinePrgUrl(e);
    return;
  };

  const inpersonClick = () => {
    dataLayerPush('goy_inperson_register');
  };

  const onlineClick = objId => {
    dataLayerPush(objId);
  };

  const handleRadioClick = (event, value, purl, rStatus) => {
    if (rStatus !== 'Full') {
      if (value !== '') {
        setCtaButtonText(value);
        setPrgUrl(purl);
      } else {
        setCtaButtonText('');
        setPrgUrl(purl);
      }
    } else {
      event.preventDefault();
    }
  };

  const dataLayerPush = eName => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eName,
    });
  };

  return (
    <Box mt="80px" id="program-details">
      <SectionTitle titleObj={sectionContent[0]} />
      {/* <SingleLineText sectionContent={sectionContent[1]} /> */}
      <HtmlBlock sectionContent={sectionContent[1]} />
      <StructuredTextParser
        str={render(sectionContent[4].body)}
        region={region}
        lang={lang}
      />

      <Box w="100%" maxW="1170px" m={{ base: '40px auto 0', md: '60px auto' }}>
        <Tabs
          isFitted
          variant="enclosed"
          p="0"
          className="tabcontent"
          border={{
            base: 'none',
            md: tabDesignHide ? 'none' : '2px solid #F0F0F0',
          }}
          borderRadius="12px"
          paddingBottom="20px"
          id="prgTab"
        >
          {!tabDesignHide && (
            <TabList borderBottom="none" mb="25px">
              <CustomTab style={{ borderRadius: '10px 0 0 0' }}>
                {sectionContent[8]?.titleText}
              </CustomTab>
              <CustomTab style={{ borderRadius: '0 10px 0 0' }}>
                {sectionContent[2]?.titleText}
              </CustomTab>
            </TabList>
          )}

          <TabPanels m="0 auto" maxW="900px">
            {sectionContent[8]?.titleDecoration !== 'tab-hide' && (
              <TabPanel>
                <HtmlBlock sectionContent={sectionContent[9]} />
                {csrData && (
                  <Grid
                    placeItems="center"
                    placeContent="center"
                    width="auto"
                    mx="auto"
                  >
                    <FormControl>
                      <RadioGroup onChange={onlinecheck} value={onlinePrgUrl}>
                        <Flex
                          placeContent="center"
                          gridGap={{ base: '15px', md: '30px' }}
                          // columns={{ lg: 3, md: 2, base: 1, sm: 2 }}
                          spacing={{ base: 8, md: 30 }}
                          mt="10px"
                        >
                          {sectionContent[10]?.data?.map((data, i) => {
                            return (
                              <Box
                                as="label"
                                key={nanoid(4)}
                                w={{ md: '279px', base: '100%' }}
                                maxW="279px"
                                minW="160px"
                                paddingLeft={{
                                  base: '12.14px',
                                  md: '21.18px',
                                }}
                                pr={{ base: '11.47px', md: '20px' }}
                                pt={{ base: '16.5px', md: '29px' }}
                                pb={{ base: '19px', md: '27px' }}
                                height="auto"
                                // style={
                                //   onlinePrgUrl === data.Title
                                //     ? cardStyle.Selected
                                //     : data.Status === 'Full'
                                //       ? cardStyle.Full
                                //       : cardStyle.Open
                                // }

                                style={cardStyle.Open}
                                position="relative"
                                borderRadius="10px"
                                cursor={
                                  data.Status === 'Full' ? 'cursor' : 'pointer'
                                }
                                _hover={{
                                  outline:
                                    data.Status !== 'Full' &&
                                    '2px solid #CF4520 !Important',
                                  boxShadow:
                                    data.Status !== 'Full' &&
                                    '3.06087px 18.7518px 41px rgba(34, 30, 2, 0.08), 1.98389px 12.154px 24.0116px rgba(34, 30, 2, 0.0607407), 1.179px 7.22293px 13.0593px rgba(34, 30, 2, 0.0485926), 0.612173px 3.75037px 6.6625px rgba(34, 30, 2, 0.04), 0.249404px 1.52793px 3.34074px rgba(34, 30, 2, 0.0314074), 0.0566827px 0.347256px 1.61343px rgba(34, 30, 2, 0.0192593) !Important',
                                  background:
                                    data.Status !== 'Full' &&
                                    '#F5F0E5 !Important',
                                }}
                              >
                                {i === 0 && csrData.goy_status !== 'open' && (
                                  <Box
                                    pos="absolute"
                                    top={0}
                                    right={0}
                                    left={0}
                                    bottom={0}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    textAlign="center"
                                    background="#494949"
                                    opacity="0.7"
                                    borderRadius="10px"
                                  >
                                    <Text
                                      color="#fff"
                                      fontSize="24px"
                                      fontFamily="FedraSansStd-medium"
                                    >
                                      FULL
                                    </Text>
                                  </Box>
                                )}

                                {i === 1 && csrData.pbk_status !== 'open' && (
                                  <Box
                                    pos="absolute"
                                    top={0}
                                    right={0}
                                    left={0}
                                    bottom={0}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    textAlign="center"
                                    background="#494949"
                                    opacity="0.7"
                                    borderRadius="10px"
                                  >
                                    <Text
                                      color="#fff"
                                      fontSize="24px"
                                      fontFamily="FedraSansStd-medium"
                                    >
                                      FULL
                                    </Text>
                                  </Box>
                                )}

                                {sectionContent[13]?._modelApiKey !==
                                  'simple_button' && (
                                  <Box
                                    pos="absolute"
                                    top={0}
                                    right={0}
                                    left={0}
                                    bottom={0}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    textAlign="center"
                                    background="#494949"
                                    opacity="0.7"
                                    borderRadius="10px"
                                  >
                                    <Text
                                      color="#fff"
                                      fontSize="24px"
                                      fontFamily="FedraSansStd-medium"
                                    >
                                      &nbsp;
                                    </Text>
                                  </Box>
                                )}
                                <style>
                                  {`
                                  .radio{
                                    border:0 !important;
                                    background:transparent !important;
                                  }
                                  .radio:before{
                                    background:#CF4520 !important;
                                  }
                                  `}
                                </style>
                                <Flex justifyContent="space-between">
                                  <Box
                                    fontFamily="FedraSansStd-medium"
                                    fontStyle="normal"
                                    fontWeight="500"
                                    fontSize={{ md: '20px', base: '14px' }}
                                    lineHeight="16px"
                                    display="flex"
                                    alignItems="center"
                                    fontFeatureSettings="case"
                                    color="#000000"
                                  >
                                    {data.Title}
                                  </Box>
                                  <Radio
                                    name={i === 0 ? 'prg1' : 'prg2'}
                                    id="radio1"
                                    className="radio"
                                    colorScheme="orange"
                                    isDisabled={
                                      i === 1 && csrData.pbk_status !== 'open'
                                        ? true
                                        : i === 0 &&
                                          csrData.goy_status !== 'open'
                                        ? true
                                        : false
                                    }
                                    value={
                                      i === 1
                                        ? csrData?.pbk_reg_url?.replace(
                                            prgDomain,
                                            `${prgDomain}${paramLang}`
                                          )
                                        : csrData?.goy_reg_url?.replace(
                                            prgDomain,
                                            `${prgDomain}${paramLang}`
                                          )
                                    }
                                    outline="1px solid #CF4520"
                                    width="26px"
                                    height="26px"
                                    key={data.EventText}
                                    cursor="pointer"
                                  />
                                </Flex>
                                <Box
                                  maxW={{ base: '110px', md: '150px' }}
                                  fontFamily="FedraSansStd-book"
                                  borderRadius="4px"
                                  fontSize={{ md: '16px', base: '14px' }}
                                  mb="10px"
                                  display="block"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  {data.Date}
                                </Box>
                                <Box
                                  maxW={{ base: '110px', md: '150px' }}
                                  fontFamily="FedraSansStd-book"
                                  background="#E4DED4"
                                  borderRadius="4px"
                                  p={{ sm: '4px 12px', base: '5px' }}
                                  fontSize={{ md: '18px', base: '12px' }}
                                  mt="20px"
                                  mb="10px"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  {i === 0 && (
                                    <Box as="span">
                                      {parseInt(
                                        csrData.goy_price
                                      ).toLocaleString('en-IN')}{' '}
                                      {csrData.currency}
                                    </Box>
                                  )}
                                  {i === 1 && (
                                    <Box
                                      as="span"
                                      minW={{ base: '110px', md: '150px' }}
                                      textAlign="center"
                                    >
                                      {parseInt(
                                        csrData.pbk_price
                                      ).toLocaleString('en-IN')}{' '}
                                      {csrData.currency}
                                    </Box>
                                  )}
                                </Box>

                                <Box>
                                  <UnorderedList
                                    fontStyle="normal"
                                    fontWeight="400"
                                    fontSize={{ md: '16px', base: '12px' }}
                                    lineHeight={{ md: '20px', base: '16px' }}
                                    fontFeatureSettings="case"
                                    color="#28231E"
                                    fontFamily="FedraSansStd-book"
                                  >
                                    {data.Descripton.value_1 != '' && (
                                      <ListItem mb="10px">
                                        {parser(data.Descripton.value_1 || '')}
                                      </ListItem>
                                    )}
                                    {data.Descripton.value_2 != '' && (
                                      <ListItem mb="10px">
                                        {parser(data.Descripton.value_2 || '')}
                                      </ListItem>
                                    )}
                                    {data.Descripton.value_3 != '' && (
                                      <ListItem mb="10px">
                                        {parser(data.Descripton.value_3 || '')}
                                      </ListItem>
                                    )}
                                  </UnorderedList>
                                </Box>
                              </Box>
                            );
                          })}
                        </Flex>
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                )}

                {csrData.goy_kit_avble === 'no' && (
                  <StructuredTextParser
                    str={render(sectionContent[11]?.body)}
                    region={region}
                    lang={lang}
                    className="structure-text"
                    mt="30px"
                    fontSize={{ md: '18px !important', base: '16px' }}
                    lineHeight="24px"
                    textAlign="justify"
                    fontStyle="italic"
                  />
                )}
                {csrData.pbk_kit_avble === 'yes' &&
                  csrData.pbk_kitprice_incl === 'no' && (
                    <StructuredTextParser
                      str={render(sectionContent[12]?.body)}
                      region={region}
                      lang={lang}
                      mt="30px"
                      fontSize={{ md: '18px !important', base: '16px' }}
                      lineHeight="24px"
                      textAlign="justify"
                      fontStyle="italic"
                      className="structure-text"
                    />
                  )}

                {sectionContent[13]?._modelApiKey === 'simple_button' && (
                  <Box display="flex" placeContent="center" mt="40px">
                    {/* <NextLink
                      href={refineUrlDomain(onlinePrgUrl, region, lang)}
                      passHref
                    > */}
                    <Link
                      onClick={e => {
                        onlineClick(onlinePrgEventText);
                      }}
                      href={refineUrlDomain(onlinePrgUrl, region, lang)}
                      mb="30px"
                      display="block"
                      fontWeight={'500'}
                      fontFamily={'FedraSansStd-medium'}
                      fontSize={'18px'}
                      bgColor={onlinePrgUrl ? '#cf4520' : '#cf452069'}
                      h={'auto'}
                      minH="50px"
                      textAlign="left"
                      padding={'14px 29px'}
                      borderRadius="5px"
                      color="#faf7f0"
                      textDecoration="none"
                      width={'auto'}
                      cursor={onlinePrgUrl ? 'pointer' : 'not-allowed'}
                      _hover={{
                        bg: onlinePrgUrl ? '#000054' : '',
                      }}
                      target={onlinePrgUrl ? '_blank' : ''}
                      id={`${onlinePrgEventText}`}
                    >
                      {sectionContent[13]?.buttonText}
                    </Link>
                    {/* </NextLink> */}
                  </Box>
                )}

                {sectionContent[13]?._modelApiKey === 'single_line_text' && (
                  <SingleLineText sectionContent={sectionContent[13]} />
                )}
              </TabPanel>
            )}
            {/* Tab 2 starts here */}
            {sectionContent[2]?.titleDecoration !== 'tab-hide' && (
              <TabPanel>
                <HtmlBlock
                  sectionContent={sectionContent[3]}
                  className={'programDetailsTime'}
                />
                <SingleImage
                  sectionContent={sectionContent[4]}
                  region={region}
                  lang={lang}
                />

                <Grid
                  placeItems="center"
                  placeContent="center"
                  width="auto"
                  mx="auto"
                >
                  <FormControl>
                    {/* <RadioGroup onChange={setPrgUrl} value={prgUrl}> */}
                    <RadioGroup
                      onChange={event => setPrgUrl(event.target.value)}
                      value={prgUrl}
                    >
                      <SimpleGrid
                        columns={{ lg: 3, md: 2, sm: 2, base: 2 }}
                        spacing={30}
                        mt="10px"
                        className="gridComponent"
                      >
                        {sectionContent[5]?.data?.map((data, i) => {
                          data.style = data.Status;
                          return (
                            data.Status !== 'Closed' && (
                              <Box
                                as={'label'}
                                key={nanoid(4)}
                                w={{ md: '279px', base: '100%' }}
                                maxWidth="279px"
                                minW="1"
                                paddingLeft={{
                                  base: '12.14px',
                                  md: '21.18px',
                                }}
                                pr={{ base: '11.47px', md: '20px' }}
                                pt={{ base: '16.5px', md: '29px' }}
                                pb={{ base: '19px', md: '27px' }}
                                height="auto"
                                style={
                                  prgUrl === data.Radio
                                    ? cardStyle.Selected
                                    : data.Status === 'Full'
                                    ? cardStyle.Full
                                    : cardStyle.Open
                                }
                                position="relative"
                                borderRadius="10px"
                                cursor={
                                  data.Status === 'Full' ? 'cursor' : 'pointer'
                                }
                                _hover={{
                                  outline:
                                    data.Status !== 'Full' &&
                                    '2px solid #CF4520 !Important',
                                  boxShadow:
                                    data.Status !== 'Full' &&
                                    '3.06087px 18.7518px 41px rgba(34, 30, 2, 0.08), 1.98389px 12.154px 24.0116px rgba(34, 30, 2, 0.0607407), 1.179px 7.22293px 13.0593px rgba(34, 30, 2, 0.0485926), 0.612173px 3.75037px 6.6625px rgba(34, 30, 2, 0.04), 0.249404px 1.52793px 3.34074px rgba(34, 30, 2, 0.0314074), 0.0566827px 0.347256px 1.61343px rgba(34, 30, 2, 0.0192593) !Important',
                                  background:
                                    data.Status !== 'Full' &&
                                    '#F5F0E5 !Important',
                                }}
                                onClick={event =>
                                  handleRadioClick(
                                    event,
                                    data.CTAText,
                                    data.Radio,
                                    data.Status
                                  )
                                }
                              >
                                {data.Status === 'Full' && (
                                  <Box
                                    pos="absolute"
                                    top={0}
                                    right={0}
                                    left={0}
                                    bottom={0}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    textAlign="center"
                                    background="#494949"
                                    opacity="0.7"
                                    borderRadius="10px"
                                  >
                                    <Text
                                      color="#fff"
                                      fontSize="24px"
                                      fontFamily="FedraSansStd-medium"
                                    >
                                      FULL
                                    </Text>
                                  </Box>
                                )}
                                <style>
                                  {`
                                    .radio{
                                      border:0 !important;
                                      background:transparent !important;
                                    }
                                    .radio:before{
                                      background:#CF4520 !important;
                                    }
                                    `}
                                </style>
                                <Flex justifyContent="space-between">
                                  <Box
                                    fontFamily="FedraSansStd-medium"
                                    fontStyle="normal"
                                    fontWeight={
                                      lang === 'ta' || lang === 'te'
                                        ? '600'
                                        : '500'
                                    }
                                    fontSize={{ md: '20px', base: '14px' }}
                                    lineHeight={
                                      pageCss[lang].cardTitle.lineHeight
                                    }
                                    display="flex"
                                    alignItems="center"
                                    fontFeatureSettings="case"
                                    color="#000000"
                                  >
                                    {data.Name}
                                  </Box>
                                  <Radio
                                    name="day"
                                    id="radio"
                                    className="radio"
                                    colorScheme="orange"
                                    isDisabled={
                                      data.Status === 'Full' ? true : false
                                    }
                                    // value={data.Radio.replace(
                                    //   prgDomain,
                                    //   `${prgDomain}${paramLang}`
                                    // )}
                                    value={data.Radio}
                                    outline="1px solid #CF4520"
                                    width="26px"
                                    height="26px"
                                    key={data.Radio}
                                    cursor="pointer"
                                  />
                                </Flex>
                                {data.ProgramFees && (
                                  <Box
                                    w={'auto'}
                                    fontFamily="FedraSansStd-book"
                                    background="#E4DED4"
                                    borderRadius="4px"
                                    p={{ sm: '4px 12px', base: '10px 10px' }}
                                    fontSize={{ md: '18px', base: '12px' }}
                                    mt="20px"
                                    mb="10px"
                                    // display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    {data.ProgramFees}
                                  </Box>
                                )}

                                <Flex justifyContent="space-between">
                                  <Box
                                    minW={{ base: '110px', md: '150px' }}
                                    p={{ sm: '4px 12px', base: 'auto' }}
                                    fontFamily="FedraSansStd-book"
                                    fontSize={{ md: '18px', base: '12px' }}
                                    background={
                                      data.ProgramFee
                                        ? '#E4DED4'
                                        : 'transparent'
                                    }
                                    borderRadius="4px"
                                    mb="20px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    {data.ProgramFee}
                                  </Box>
                                  <Text
                                    py="2px"
                                    fontSize={{ md: '18px', base: '12px' }}
                                    style={{
                                      fontWeight: '400',
                                      lineHeight: '22px',
                                    }}
                                    fontFamily="FedraSansStd-book"
                                  >
                                    {data?.Couple}
                                  </Text>
                                </Flex>

                                <Box>
                                  <UnorderedList
                                    fontStyle="normal"
                                    fontWeight="400"
                                    fontSize={{ md: '16px', base: '12px' }}
                                    lineHeight={{ md: '20px', base: '16px' }}
                                    fontFeatureSettings="case"
                                    color="#28231E"
                                    fontFamily="FedraSansStd-book"
                                    pl={'4px'}
                                  >
                                    {data.Descripton.value_1 != '' && (
                                      <ListItem mb="10px">
                                        {parser(data.Descripton.value_1 || '')}
                                      </ListItem>
                                    )}
                                    {data.Descripton.value_2 != '' && (
                                      <ListItem mb="10px">
                                        {parser(data.Descripton.value_2 || '')}
                                      </ListItem>
                                    )}
                                    {data.Descripton.value_3 != '' && (
                                      <ListItem mb="10px">
                                        {parser(data.Descripton.value_3 || '')}
                                      </ListItem>
                                    )}
                                    {data.Descripton.value_4 != '' && (
                                      <ListItem mb="10px">
                                        {parser(data.Descripton.value_4 || '')}
                                      </ListItem>
                                    )}
                                  </UnorderedList>
                                </Box>
                              </Box>
                            )
                          );
                        })}
                      </SimpleGrid>
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <MidContainer w={'768'}>
                  <StructuredTextParser
                    str={render(sectionContent[6].body)}
                    region={region}
                    lang={lang}
                    mt="30px"
                    className="programDetailsText"
                    textAlign="center"
                  />
                  <style>
                    {`
                      .programDetailsText p{
                        font-size:18px;
                      }
                      @media screen and (max-width: 450px) {
                        .programDetailsText{
                          text-align:left;
                        }
                        .programDetailsText p{
                          font-size:14px !important;
                        }
                        .programDetailsText strong{
                          font-size:14px !important;
                        }
                      }
                      `}
                  </style>
                  {sectionContent[7]?._modelApiKey === 'simple_button' && (
                    <Box display="flex" placeContent="center" mt="40px">
                      {/* <NextLink
                        href={refineUrlDomain(prgUrl, region, lang)}
                        passHref
                      > */}
                      <Link
                        onClick={e => {
                          inpersonClick();
                        }}
                        href={refineUrlDomain(prgUrl, region, lang)}
                        mb="30px"
                        display="block"
                        fontWeight={'500'}
                        fontFamily={'FedraSansStd-medium'}
                        fontSize={'18px'}
                        bgColor={prgUrl ? '#cf4520' : '#cf452069'}
                        h={'auto'}
                        minH="50px"
                        textAlign="left"
                        padding={'14px 29px'}
                        borderRadius="5px"
                        color="#faf7f0"
                        textDecoration="none"
                        width={'auto'}
                        cursor={prgUrl ? 'pointer' : 'not-allowed'}
                        _hover={{
                          bg: prgUrl ? '#000054' : '',
                        }}
                        target={prgUrl ? '_blank' : ''}
                        // id="goy_inperson_register"
                      >
                        {ctaButtonText
                          ? ctaButtonText
                          : sectionContent[7]?.buttonText}
                      </Link>
                      {/* </NextLink> */}
                    </Box>
                  )}
                </MidContainer>
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      </Box>
      <style>
        {`
          @media only screen and (max-width: 360px) {
            .gridComponent {
              grid-template-columns: repeat(1, minmax(0, 1fr));
    margin-top: 10px;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default ProgramDetails;
